/**
 * Appointment status as the Calendar module writes it (calendar_events.statusByDate:
 * 'scheduled', 'confirmed', 'checked-in', 'checked-out' (calendar#61), 'completed',
 * 'no-show', 'client-cancelled', 'clinician-cancelled', 'late-cancel', or a practice's
 * own custom status) plus the older core values ('no_show', 'cancelled'). Today's
 * Schedule and the client drawer used to compare against 'no_show' / 'cancelled' only,
 * so a calendar no-show, cancellation or check-in read "Not confirmed".
 */

export type ApptTone =
  'scheduled' | 'confirmed' | 'checkedin' | 'checkedout' | 'completed' | 'noshow' | 'cancelled';

export interface ApptStatusInfo {
  label: string;
  tone: ApptTone;
  /** Cancelled outright or late-cancelled: greyed out, no note. */
  cancelled: boolean;
}

const KNOWN: Record<string, [string, ApptTone]> = {
  scheduled: ['Scheduled', 'scheduled'],
  confirmed: ['Confirmed', 'confirmed'],
  'checked-in': ['Checked in', 'checkedin'],
  'checked-out': ['Checked out', 'checkedout'],
  completed: ['Completed', 'completed'],
  'no-show': ['No-show', 'noshow'],
  no_show: ['No-show', 'noshow'],
  'client-cancelled': ['Client cancelled', 'cancelled'],
  'clinician-cancelled': ['Clinician cancelled', 'cancelled'],
  'late-cancel': ['Late cancel', 'cancelled'],
  cancelled: ['Cancelled', 'cancelled'],
};

/** "my-custom_status" → "My custom status" (a practice's own Calendar status). */
const humanize = (s: string) => {
  const t = s.replace(/[-_]+/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Scheduled';
};

export function apptStatusInfo(status: string | null | undefined): ApptStatusInfo {
  const key = (status ?? '').trim() || 'scheduled';
  const hit = KNOWN[key];
  if (hit) return { label: hit[0], tone: hit[1], cancelled: hit[1] === 'cancelled' };
  return { label: humanize(key), tone: 'scheduled', cancelled: false };
}
